// frontend/src/pages/booking/BookingFlow.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../../services/api';

import DateTimeSelection from './steps/DateTimeSelection';
import ATVSelection from './steps/ATVSelection';
import BookingForm from './steps/BookingForm';
import WaiverSigning from './steps/WaiverSigning';
import PaymentStep from './steps/PaymentStep';
import ConfirmationStep from './steps/ConfirmationStep';
import ProgressIndicator from './components/ProgressIndicator';

const BookingFlow = () => {
  const { bookingRef } = useParams();
  const navigate = useNavigate();

  const [tour, setTour] = useState(null);
  const [currentStep, setCurrentStep] = useState('datetime');
  const [bookingData, setBookingData] = useState({
    reference: bookingRef !== 'new' ? bookingRef : null,
    tour: null,
    selectedDate: null,
    selectedTime: null,
    selectedSlot: null,
    availabilityId: null,
    rideType: null,
    numAtvs: 0,
    numRiders: 0,
    numPassengers: 0,
    customerInfo: null,
    participants: [],
    totalAmount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const steps = [
    { id: 'datetime', title: 'Date & Time', icon: '📅' },
    { id: 'atvs', title: 'Select ATVs', icon: '🏍️' },
    { id: 'details', title: 'Rider Details', icon: '📝' },
    { id: 'waivers', title: 'Waivers', icon: '✍️' },
    { id: 'payment', title: 'Payment', icon: '💳' },
    { id: 'confirmation', title: 'Confirmed', icon: '✅' }
  ];

  useEffect(() => {
    const loadTourData = async () => {
      try {
        setLoading(true);

        if (bookingRef === 'new') {
          const storedTour = sessionStorage.getItem('selectedTour');
          if (storedTour) {
            const parsedTour = JSON.parse(storedTour);
            setTour(parsedTour);
            setBookingData(prev => ({ ...prev, tour: parsedTour }));
          } else {
            setError('No tour selected. Please select a tour first.');
          }
        } else {
          await loadExistingBooking();
        }
      } catch (err) {
        setError(err.message || 'Failed to load tour data');
      } finally {
        setLoading(false);
      }
    };

    loadTourData();
  }, [bookingRef]);

  const loadExistingBooking = async () => {
    try {
      const response = await API.bookings.get(bookingRef);
      const booking = response.data;

      const tourResponse = await API.tours.getBySlug(booking.tour_slug);
      const tourData = tourResponse.data;

      setTour(tourData);
      setBookingData({
        reference: booking.booking_reference,
        tour: tourData,
        selectedDate: booking.date,
        selectedTime: booking.time_slot,
        availabilityId: booking.availability_id,
        customerInfo: {
          name: booking.customer_name,
          email: booking.customer_email,
          phone: booking.customer_phone,
          pickup_location: booking.pickup_location,
          special_requirements: booking.special_requirements
        },
        participants: booking.participants,
        totalAmount: booking.total_amount
      });

      if (booking.payment_status === 'paid') {
        setCurrentStep('confirmation');
      } else if (booking.waiver_completed) {
        setCurrentStep('payment');
      } else if (booking.participants && booking.participants.length > 0) {
        setCurrentStep('waivers');
      } else {
        setCurrentStep('details');
      }
    } catch (err) {
      throw new Error(err.message || 'Failed to load booking');
    }
  };

  const handleStepComplete = (stepData) => {
    setBookingData(prev => ({ ...prev, ...stepData }));

    const currentIndex = steps.findIndex(s => s.id === currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToStep = (stepId) => {
    const targetIndex = steps.findIndex(s => s.id === stepId);
    const currentIndex = steps.findIndex(s => s.id === currentStep);

    if (targetIndex <= currentIndex) {
      setCurrentStep(stepId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-white">Loading booking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Oops!</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/tours')}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-bold transition-colors"
          >
            Back to Tours
          </button>
        </div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-white">Loading tour information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4">
        <ProgressIndicator
          steps={steps}
          currentStep={currentStep}
          onStepClick={goToStep}
        />

        <div className="mt-8">
          {currentStep === 'datetime' && (
            <DateTimeSelection tour={tour} onComplete={handleStepComplete} />
          )}
          {currentStep === 'atvs' && (
            <ATVSelection tour={tour} bookingData={bookingData} onComplete={handleStepComplete} onBack={() => goToStep('datetime')} />
          )}
          {currentStep === 'details' && (
            <BookingForm tour={tour} bookingData={bookingData} onComplete={handleStepComplete} onBack={() => goToStep('atvs')} />
          )}
          {currentStep === 'waivers' && (
            <WaiverSigning bookingData={bookingData} onComplete={handleStepComplete} onBack={() => goToStep('details')} />
          )}
          {currentStep === 'payment' && (
            <PaymentStep bookingData={bookingData} onComplete={handleStepComplete} onBack={() => goToStep('waivers')} />
          )}
          {currentStep === 'confirmation' && (
            <ConfirmationStep bookingData={bookingData} />
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingFlow;